import { useCallback, useMemo } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from '@atrvd/saas-ui'
import { createTenantDb } from '@/lib/db-tenant'
import type {
  BankConnectionRow,
  BankConnectionSortColumn,
  ConnectionAccountRow,
  ConnectionAccountsCursor,
  ConnectionAccountsPage,
  ConnectionAccountsQuery,
  SortDirection,
} from '@/lib/db-tenant'
import { CONNECTION_ACCOUNTS_MAX_PAGE_SIZE } from '@/lib/db-tenant'
import { BANKING_KEYS } from '@/lib/query-keys'
import { deleteLedgerSyncConnection } from '@/hooks/useLedgerSync'
import { useOrganization } from '@/contexts/OrganizationContext'
import { flattenPages } from '@/hooks/useInfiniteScroll'
import { pollWhileActive } from '@/hooks/useBackgroundJobStatus'
import { anyConnectionSyncActive } from '@/lib/ledgersync-sync-activity'
import { hasPendingSyncClick } from '@/lib/ledgersync-sync-clicks'
import { invalidateAccountDependents } from '@/lib/ledgersync-sync-invalidation'
import { countAccountsByConnection, type FacilityAccountCounts } from '@/lib/facility-account-counts'

/**
 * React Query layer for the DCP-324 connection-management screens.
 *
 * The Supabase side is already built (`db-tenant.ts`:
 * `ledgerSyncConnections.*` / `ledgerSyncAccounts.*`) and is NOT reimplemented
 * here — these hooks only own caching, invalidation and the user-facing toast
 * for each write.
 *
 * INVALIDATION CONTRACT. Every mutation invalidates by PREFIX
 * (`allBankConnections` / `allConnectionAccounts`), never by an exact key. The
 * connection list is keyed by its sort and the accounts table by its whole
 * filter+sort+cursor set, so a single write can invalidate dozens of live cache
 * entries and the caller has no way to enumerate them. Prefix keys were chosen
 * for exactly this in `query-keys.ts` — see the note there about DCP-333, where
 * a helper with a trailing `undefined` stopped being a prefix of its own scoped
 * entries and every invalidation silently matched nothing.
 */

// ---------------------------------------------------------------------------
// Connection list (Screen 1)
// ---------------------------------------------------------------------------

/**
 * Poll period of the connection list while a sync is in flight (DCP-401). The
 * phases last seconds to many minutes; 3s makes a transition visible promptly
 * for a request that returns tens of rows.
 */
export const BANK_CONNECTIONS_SYNC_POLL_MS = 3000

/**
 * How long the list keeps polling after a sync the user started HERE, even
 * while no row shows it active (DCP-401).
 *
 * WHY A GRACE WINDOW AT ALL. The edge stamps `queued` for every NEW run before
 * it answers, but that write is deliberately non-blocking: if it fails, the
 * run is enqueued while the row still reads its previous state. With the poll
 * keyed on status alone it would never start, and that run's finish would
 * never be seen. The window is the safety net for that one case.
 *
 * WHY 30 SECONDS. The gap to cover is enqueue → the run's first stamp: the
 * orchestrator writes `refreshing` as its first act, BEFORE the long refresh
 * wait, so the window only has to outlast the Trigger.dev dequeue on an idle
 * `ledgersync-wait` queue — seconds in practice. Once `refreshing` shows, the
 * status-based poll takes over for as long as the run lives. 30s is several
 * times that latency at a bounded cost (≤10 extra requests per click). A run
 * stuck in the queue longer than that — behind a 07:00 sweep — is missed
 * until the next focus/refetch shows it; that is the harmless direction.
 */
export const BANK_CONNECTIONS_START_GRACE_MS = 30_000

/**
 * The org's bank connections, sorted SERVER-SIDE (AC#4).
 *
 * The sort is part of the query key because the RPC does the ordering: two sort
 * orders are two different server responses, not two views of one cached array.
 * Deliberately unpaged — an org has tens of connections, and the list's whole
 * job is to be scanned at a glance.
 *
 * @param sort.column - Sortable column; the data layer defaults to `display_name`
 * @param sort.direction - `asc` / `desc`; the data layer defaults to `asc`
 */
export function useBankConnections(sort: {
  column?: BankConnectionSortColumn
  direction?: SortDirection
} = {}) {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useQuery<BankConnectionRow[]>({
    queryKey: BANKING_KEYS.bankConnections(
      currentOrganization?.id,
      sort.column,
      sort.direction,
    ),
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.ledgerSyncConnections.listForManagement({
        sortColumn: sort.column,
        sortDirection: sort.direction,
      })
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!currentOrganization?.id,
    // DCP-401: while ANY connection has a pipeline in flight (per the
    // stall-aware shared rule), re-read the list so its status moves through
    // queued → waiting for bank → syncing → done on screen, and the completion
    // watcher sees the finish. Stops by itself on the response that shows
    // nothing running — including a run that died and went stale. Only the
    // mounted key polls, so other sort orders cached earlier stay quiet.
    // Also polls through the short grace window after a Sync click in THIS
    // tab that has not settled yet, for a new run the edge enqueued without stamping `queued`
    // (see BANK_CONNECTIONS_START_GRACE_MS).
    refetchInterval: pollWhileActive<BankConnectionRow[]>(
      (rows) =>
        anyConnectionSyncActive(rows) ||
        hasPendingSyncClick(queryClient, currentOrganization?.id, BANK_CONNECTIONS_START_GRACE_MS),
      BANK_CONNECTIONS_SYNC_POLL_MS,
    ),
    // This query only, not the global default (queryClient.ts keeps it off): a
    // sync nobody on this tab started — the 07:00 schedule, another user —
    // is picked up when the user returns to the tab, which then starts the
    // poll above if it is still running. `'always'`, not `true`: `true` only
    // refetches STALE data, and under the global 5-minute staleTime a user who
    // glanced away for four minutes would come back to the pre-sync list.
    refetchOnWindowFocus: 'always',
  })
}

/**
 * One connection from the list, by id.
 *
 * Reads the LIST rather than fetching a single row: the detail screen is only
 * ever reached from that list, so the row is already cached, and the read model
 * has no single-row RPC to call. The default (unsorted) key is used so the
 * detail page shares one cache entry however the list happened to be sorted.
 *
 * Also hands back the whole list as `connections` (DCP-401), for the page's
 * completion watcher: reading it from here rather than a second
 * `useBankConnections()` keeps ONE observer — and so one poll timer — on the
 * list while a sync runs.
 *
 * @param connectionId - `ledgersync_connections.id`
 */
export function useBankConnection(connectionId: string | undefined) {
  const query = useBankConnections()
  const connection = useMemo(
    () => (connectionId ? (query.data ?? []).find((row) => row.id === connectionId) : undefined),
    [query.data, connectionId],
  )
  return { ...query, data: connection, connections: query.data }
}

/**
 * How many live accounts each connection has at one facility — the facility
 * page's "Accounts & cards" figure and the rule for which connections it lists.
 * Idle (no request) without a facility, i.e. on the org-wide page.
 *
 * Refreshed by {@link invalidateAccountDependents}, which every write to a
 * connection's accounts already runs.
 *
 * @param facilityId - `facilities.id`, or null when the page is not facility-scoped
 */
export function useFacilityConnectionAccountCounts(facilityId: string | null) {
  const { currentOrganization } = useOrganization()
  return useQuery<FacilityAccountCounts>({
    queryKey: BANKING_KEYS.facilityConnectionAccountCounts(currentOrganization?.id, facilityId ?? ''),
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      if (!facilityId) throw new Error('No facility selected')
      const { data, error } = await createTenantDb(
        currentOrganization.id,
      ).ledgerSyncAccounts.connectionIdsAtFacility(facilityId)
      if (error) throw new Error(error.message)
      return countAccountsByConnection(data)
    },
    enabled: !!currentOrganization?.id && !!facilityId,
  })
}

/** AC#5: rename a connection. */
export function useRenameBankConnection() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncConnections.rename(id, name)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
      toast.success('Connection renamed')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to rename connection')
    },
  })
}

/**
 * Is this the refusal `setInitialImportFrom` returns once the connection's
 * initial import has started (DCP-403)?
 *
 * @param error - an error thrown by {@link useSetInitialImportFrom}
 */
export function isInitialImportDoneError(error: unknown): boolean {
  return error instanceof Error && (error as Error & { code?: string }).code === 'INITIAL_IMPORT_DONE'
}

/**
 * AC#9/AC#23: set how far back the connection's first sync should reach.
 *
 * No success toast. The field saves on change, so a toast per keystroke-settled
 * edit would be noise; a FAILURE still surfaces, because a silently unsaved
 * import window is the one outcome the user must not miss.
 *
 * The thrown error keeps the db layer's `code`, so a caller can tell "the
 * initial import has already started" (`INITIAL_IMPORT_DONE`) from a bad date.
 * On that refusal the lock query is re-asked here, so the field locks instead
 * of staying editable for another doomed attempt.
 */
export function useSetInitialImportFrom() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, date }: { id: string; date: string | null }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncConnections.setInitialImportFrom(id, date)
      if (error) throw Object.assign(new Error(error.message), { code: error.code })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allBankConnections })
    },
    onError: (error) => {
      if (isInitialImportDoneError(error)) {
        // The lock lives under the connection's accounts
        // (useConnectionInitialImportDone).
        queryClient.invalidateQueries({ queryKey: BANKING_KEYS.allConnectionAccounts })
      }
      toast.error(error instanceof Error ? error.message : 'Failed to save the import date')
    },
  })
}

/**
 * Shown when the local delete succeeded but LedgerSync could not be told about
 * it. Deliberately specific about the consequence — the bank is gone from DCP,
 * but the aggregator may still be pulling from it, so the user (or support) has
 * to finish the job at the source.
 */
const REVOKE_FAILED_WARNING =
  'The connection was removed, but the bank link could not be revoked at LedgerSync. It may keep refreshing until it is removed there.'

/**
 * AC#7: delete a connection. The connection row is REMOVED outright; its
 * `bank_accounts` are soft-deleted and its transactions are kept.
 *
 * THE ONLY MUTATION ON THESE SCREENS THAT IS NOT A `db-tenant` WRITE, and the
 * reason is the point of the operation: deleting a connection must REVOKE the
 * bank link at LedgerSync, which needs the API key, which cannot be in the
 * browser. Before this, the delete was four PostgREST calls from here and told
 * the aggregator nothing — the bank vanished from our UI while the link stayed
 * authorized, kept refreshing, kept counting against billing, and the
 * customer's consent was never actually withdrawn. Both halves now happen in
 * one edge call (`ledgersync-link` → `delete-connection`) so a failure cannot
 * leave the two sides disagreeing.
 *
 * The revoke is BEST-EFFORT server-side: a LedgerSync outage must not stop a
 * user removing their own bank, so the local delete proceeds and the failure
 * comes back as `revokeError` — surfaced here as a WARNING alongside the
 * success, never as an error (the delete really did succeed).
 *
 * Invalidates the account-dependent caches as well as the list: the connection's
 * accounts are soft-deleted with it and their `balances` are hard-deleted, so
 * every figure derived from them changes in the same instant — Bank Balance and
 * Cash Position, but also Cross-Checking (which compares those transactions
 * against the balances that just went away) and LOC Usage (whose eligible-account
 * predicate excludes accounts under a deleted connection, AC#21). All of them
 * are covered by {@link invalidateAccountDependents}; see the WHY there.
 */
export function useDeleteBankConnection() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      return deleteLedgerSyncConnection(id, currentOrganization.id)
    },
    onSuccess: (result) => {
      invalidateAccountDependents(queryClient)
      toast.success('Connection deleted')
      if (result.revokeError) toast.warning(REVOKE_FAILED_WARNING)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete connection')
    },
  })
}

// ---------------------------------------------------------------------------
// Accounts of one connection (Screen 2)
// ---------------------------------------------------------------------------

/** Everything the accounts table needs from the paged query. */
export interface ConnectionAccountsResult {
  /** Every row fetched so far, deduped and in server order. */
  rows: ConnectionAccountRow[]
  /** Rows matching the CURRENT filter set server-side (not just the loaded ones). */
  totalCount: number
  /** True while another page exists. */
  hasMore: boolean
  /** True while any fetch is in flight. */
  isFetching: boolean
  /** True only for the very first page. */
  isLoading: boolean
  error: Error | null
  /** Ask for the next page. */
  loadMore: () => void
}

/**
 * One connection's accounts, lazily loaded page by page (AC#11/AC#12).
 *
 * `useInfiniteQuery` does the accumulating rather than a hand-rolled array in
 * component state, for three reasons that all bite the hand-rolled version:
 * it will not start a second fetch for the same page while one is in flight, it
 * discards every accumulated page when the FILTER changes (a new query key, so
 * page 1 of the new filter can never be appended to page 3 of the old one), and
 * an invalidation after a mutation refetches the pages the user has actually
 * loaded instead of resetting them to the top.
 *
 * The page param IS the keyset cursor from the previous page's last row, exactly
 * as the data layer produces it — never reconstructed from a row's fields.
 *
 * @param connectionId - `ledgersync_connections.id`
 * @param query - Filters + sort. `cursor` and `pageSize` are owned here and
 *   ignored if supplied.
 */
export function useConnectionAccounts(
  connectionId: string | undefined,
  query: Omit<ConnectionAccountsQuery, 'cursor' | 'pageSize'>,
): ConnectionAccountsResult {
  const { currentOrganization } = useOrganization()

  // The cache key must be the FILTER, not the page: `useInfiniteQuery` stores
  // every page of one filter under a single key. Spelled out field by field so
  // an added filter cannot silently join the key by object identity — and
  // stringified arrays so two equal selections in different render passes are
  // the same key.
  const queryKeyPart = useMemo(
    () => ({
      status: query.status,
      facilityIds: query.facilityIds?.join('|'),
      packIds: query.packIds?.join('|'),
      search: query.search,
      sortColumn: query.sortColumn,
      sortDirection: query.sortDirection,
    }),
    [
      query.status,
      query.facilityIds,
      query.packIds,
      query.search,
      query.sortColumn,
      query.sortDirection,
    ],
  )

  const infinite = useInfiniteQuery<
    ConnectionAccountsPage,
    Error,
    { pages: ConnectionAccountsPage[] },
    readonly unknown[],
    ConnectionAccountsCursor | null
  >({
    queryKey: BANKING_KEYS.connectionAccountsScoped(
      currentOrganization?.id,
      connectionId ?? '',
      queryKeyPart,
    ),
    queryFn: async ({ pageParam }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      if (!connectionId) throw new Error('No connection selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { data, error } = await tenantDb.ledgerSyncAccounts.listByConnection(connectionId, {
        ...query,
        pageSize: CONNECTION_ACCOUNTS_MAX_PAGE_SIZE,
        cursor: pageParam,
      })
      if (error) throw new Error(error.message)
      return data
    },
    initialPageParam: null,
    // `null` ends the paging — the data layer already applies the keyset rule
    // (short page = last page) in `nextConnectionAccountsCursor`.
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!currentOrganization?.id && !!connectionId,
  })

  const rows = useMemo(
    () => flattenPages(infinite.data?.pages, (page) => page.rows, (row) => row.id),
    [infinite.data?.pages],
  )

  // The total comes from the FIRST page: every row of every page carries the
  // same filter-set total, and the first page is the one that is never empty
  // while there is anything to count. A later page that came back empty reports
  // 0, so reading the last page instead would blank the count at the end of the
  // list.
  const totalCount = infinite.data?.pages[0]?.totalCount ?? 0

  // Destructured so the dependency is the STABLE function React Query hands
  // back, not the query object (a fresh reference every render, which would
  // rebuild this callback each time).
  const { fetchNextPage } = infinite
  const loadMore = useCallback(() => {
    void fetchNextPage()
  }, [fetchNextPage])

  return {
    rows,
    totalCount,
    hasMore: !!infinite.hasNextPage,
    isFetching: infinite.isFetching || infinite.isFetchingNextPage,
    isLoading: infinite.isLoading,
    error: infinite.error,
    loadMore,
  }
}

/**
 * Page cap of {@link readAllConnectionAccounts}: 20 pages x 200 = 4000 accounts.
 * Far beyond any real bank login; it exists so a bug in the cursor can't spin
 * a query forever.
 */
const MAX_CONNECTION_ACCOUNT_PAGES = 20

/**
 * Read EVERY account of a connection matching `query`, page by page through
 * `listByConnection` at max page size — in practice one request.
 *
 * @param accounts - The tenant's `ledgerSyncAccounts` data access
 * @param connectionId - `ledgersync_connections.id`
 * @param query - Filters and sort; paging is this function's
 * @returns The rows read, and `truncated` when the page cap stopped the read
 *   before the last page
 */
async function readAllConnectionAccounts(
  accounts: ReturnType<typeof createTenantDb>['ledgerSyncAccounts'],
  connectionId: string,
  query: Omit<ConnectionAccountsQuery, 'pageSize' | 'cursor'>,
): Promise<{ rows: ConnectionAccountRow[]; truncated: boolean }> {
  const rows: ConnectionAccountRow[] = []
  let cursor: ConnectionAccountsCursor | null = null
  for (let page = 0; page < MAX_CONNECTION_ACCOUNT_PAGES; page++) {
    const { data, error } = await accounts.listByConnection(connectionId, {
      ...query,
      pageSize: CONNECTION_ACCOUNTS_MAX_PAGE_SIZE,
      cursor,
    })
    if (error) throw new Error(error.message)
    rows.push(...data.rows)
    if (!data.nextCursor) return { rows, truncated: false }
    cursor = data.nextCursor
  }
  return { rows, truncated: true }
}

/**
 * The oldest transaction date across ALL of a connection's accounts — the floor
 * of the "Initial import from" field (AC#9).
 *
 * WHY IT IS ITS OWN QUERY and not derived from the table above. The table is
 * filtered (Connected by default) and lazily paged, so a minimum taken over its
 * loaded rows would be the oldest date among *some* accounts — always equal to
 * or LATER than the true floor, which would silently forbid a history depth the
 * connection really has. The floor is a property of the connection, so it is
 * read over the connection's accounts unfiltered (`status: 'all'`).
 *
 * The read model has no aggregate for this (`get_bank_connections` returns no
 * date), so it pages through the same RPC at max page size. In practice that is
 * ONE request — 200 accounts under a single bank login would be exceptional —
 * and it is capped so a pathological connection cannot turn the detail page into
 * an unbounded fetch loop.
 *
 * @param connectionId - `ledgersync_connections.id`
 */
export function useConnectionOldestTransactionDate(connectionId: string | undefined) {
  const { currentOrganization } = useOrganization()
  return useQuery<string | null>({
    queryKey: [
      ...BANKING_KEYS.connectionAccounts(currentOrganization?.id, connectionId ?? ''),
      'oldest-transaction-date',
    ],
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      if (!connectionId) throw new Error('No connection selected')
      // A truncated read still yields a floor (over the accounts read): the
      // cap only guards against a looping cursor, so the field stays usable.
      const { rows } = await readAllConnectionAccounts(
        createTenantDb(currentOrganization.id).ledgerSyncAccounts,
        connectionId,
        { status: 'all' },
      )
      let oldest: string | null = null
      for (const row of rows) {
        const date = row.oldest_transaction_date
        if (date && (!oldest || date < oldest)) oldest = date
      }
      return oldest
    },
    enabled: !!currentOrganization?.id && !!connectionId,
  })
}

/**
 * Every account of a connection that has NO facility, connected or
 * disconnected — what "Add account to {Facility}" offers.
 *
 * Read in full (not lazily like the table) because the picker searches and
 * selects on the client; pages through the same RPC as
 * {@link useConnectionOldestTransactionDate} ({@link readAllConnectionAccounts}).
 *
 * @param connectionId - `ledgersync_connections.id`
 */
export function useUnassignedConnectionAccounts(connectionId: string) {
  const { currentOrganization } = useOrganization()
  return useQuery<ConnectionAccountRow[]>({
    queryKey: BANKING_KEYS.unassignedConnectionAccounts(currentOrganization?.id, connectionId),
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const { rows, truncated } = await readAllConnectionAccounts(
        createTenantDb(currentOrganization.id).ledgerSyncAccounts,
        connectionId,
        {
          status: 'all',
          // A null entry is "Unassigned" (see ConnectionAccountsQuery.facilityIds).
          facilityIds: [null],
          sortColumn: 'name',
          sortDirection: 'asc',
        },
      )
      // A partial list would silently hide accounts from the picker.
      if (truncated) throw new Error('Too many unassigned accounts to list')
      return rows
    },
    enabled: !!currentOrganization?.id && !!connectionId,
  })
}

/**
 * Has this connection's INITIAL transaction import started — run or still
 * running (DCP-403)? Once it has, "Initial import from" governs nothing and the
 * field is locked.
 *
 * Keyed under the connection's accounts, so every `allConnectionAccounts`
 * invalidation re-asks it. The first import is started and finished by a
 * background job, though, so nothing on this page is told when it happens;
 * `refetchOnWindowFocus: 'always'` re-asks each time the user comes back to the
 * tab, however fresh the cached answer, and a save the server refuses as
 * `INITIAL_IMPORT_DONE` re-locks the field on the spot
 * ({@link useSetInitialImportFrom}).
 *
 * @param connectionId - `ledgersync_connections.id`
 */
export function useConnectionInitialImportDone(connectionId: string | undefined) {
  const { currentOrganization } = useOrganization()
  return useQuery<boolean>({
    queryKey: [
      ...BANKING_KEYS.connectionAccounts(currentOrganization?.id, connectionId ?? ''),
      'initial-import-done',
    ],
    queryFn: async () => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      if (!connectionId) throw new Error('No connection selected')
      const { data, error } = await createTenantDb(
        currentOrganization.id,
      ).ledgerSyncConnections.hasImportedTransactions(connectionId)
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!currentOrganization?.id && !!connectionId,
    refetchOnWindowFocus: 'always',
  })
}

/** AC#13/AC#18: set `account_type` on one or many accounts. */
export function useSetAccountType() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      ids,
      accountType,
    }: {
      ids: string[]
      accountType: ConnectionAccountRow['account_type']
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncAccounts.setAccountType(ids, accountType)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, variables) => {
      invalidateAccountDependents(queryClient)
      toast.success(
        variables.ids.length === 1
          ? 'Account type updated'
          : `Account type updated for ${variables.ids.length} accounts`,
      )
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update account type')
    },
  })
}

/** AC#14/AC#18: assign (or clear) the facility of one or many accounts. */
export function useAssignAccountFacility() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ ids, facilityId }: { ids: string[]; facilityId: string | null }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncAccounts.assignFacility(ids, facilityId)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, variables) => {
      invalidateAccountDependents(queryClient)
      toast.success(
        variables.ids.length === 1
          ? 'Facility updated'
          : `Facility updated for ${variables.ids.length} accounts`,
      )
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to assign facility')
    },
  })
}

/**
 * AC#15: toggle whether accounts count towards Cash Position.
 *
 * The data layer REFUSES credit cards with a named error; the UI disables the
 * toggle for them, so reaching that refusal means the two disagreed and the
 * error is surfaced rather than swallowed.
 */
export function useSetIncludeInCashPosition() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ ids, include }: { ids: string[]; include: boolean }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncAccounts.setIncludeInCashPosition(ids, include)
      if (error) throw new Error(error.message)
    },
    // Returned, so the row's toggle stays busy until the list has refetched and
    // does not flip back to the old value in between.
    onSuccess: () => invalidateAccountDependents(queryClient),
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update Cash Position inclusion',
      )
    },
  })
}

/** AC#16/AC#18: stop syncing one or many accounts. */
export function useDisconnectAccounts() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncAccounts.disconnect(ids)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, ids) => {
      invalidateAccountDependents(queryClient)
      toast.success(ids.length === 1 ? 'Account disconnected' : `${ids.length} accounts disconnected`)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to disconnect accounts')
    },
  })
}

/** AC#16/AC#18: resume syncing one or many accounts. */
export function useReconnectAccounts() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncAccounts.reconnect(ids)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, ids) => {
      invalidateAccountDependents(queryClient)
      toast.success(ids.length === 1 ? 'Account reconnected' : `${ids.length} accounts reconnected`)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to reconnect accounts')
    },
  })
}

/**
 * AC#17/AC#18: permanently delete one or many accounts, with their transactions
 * and balances. The caller is expected to have confirmed.
 */
export function useDeleteAccounts() {
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!currentOrganization?.id) throw new Error('No organization selected')
      const tenantDb = createTenantDb(currentOrganization.id)
      const { error } = await tenantDb.ledgerSyncAccounts.permanentlyDelete(ids)
      if (error) throw new Error(error.message)
    },
    onSuccess: (_data, ids) => {
      invalidateAccountDependents(queryClient)
      // The ONE key this mutation needs beyond the shared set: the transaction
      // rows themselves are gone (this is the only path that hard-deletes
      // `transactions`), so the dashboard's transaction table has fewer rows to
      // show. A connection delete deliberately does NOT invalidate this — it
      // KEEPS the transactions (AC#7) and they stay renderable through the
      // soft-deleted account rows (the DCP-222 invariant), so that list is
      // unchanged there. Cross-Checking / LOC Usage live in
      // `invalidateAccountDependents` above.
      queryClient.invalidateQueries({ queryKey: ['plaid', 'transactions'] })
      toast.success(ids.length === 1 ? 'Account deleted' : `${ids.length} accounts deleted`)
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete accounts')
    },
  })
}
